import React, { useEffect } from 'react';
import { useArticleList } from '../hooks/use-article-list';
import ArticlePreviewListUI from './ArticlePreviewListUI';
import Loader from './LoadingIndicator';

interface ArticlePreviewListProps {
    dateFrom?: Date;
    dateTo?: Date;
    premiumChannelId?: string;
    tagId?: string;
    deskId?: string;
    contributorId?: string;
    batchSize?: number;
    onDataLoaded?: (totalCount: number) => void;
    source: string;
    onlyMySubscriptions?: boolean;
    searchQuery?: string;
    limit?: number;
    showPagination?: boolean;
}

const ArticlePreviewList = ({
    dateFrom,
    dateTo,
    premiumChannelId,
    tagId,
    deskId,
    contributorId,
    batchSize = 9,
    source,
    onlyMySubscriptions,
    searchQuery,
    limit,
    onDataLoaded,
    showPagination,
}: ArticlePreviewListProps) => {
    const { articles, loading, hasNextPage, fetchMore, totalCount } =
        useArticleList({
            batchSize,
            dateFrom: dateFrom?.getTime().toString(),
            dateTo: dateTo?.getTime().toString(),
            premiumChannelId,
            tagId,
            deskId,
            contributorId,
            onlyMySubscriptions: onlyMySubscriptions,
            searchQuery,
        });

    useEffect(() => {
        if (!loading && onDataLoaded) {
            onDataLoaded?.(totalCount ?? 0);
        }
    }, [totalCount, loading, onDataLoaded]);

    if (loading) {
        return <Loader minHeight={128} />;
    }
    if (!articles?.length) return null;

    const limitedArticles =
        limit && articles.length >= limit ? articles.slice(0, limit) : articles;

    const total =
        limit && totalCount
            ? totalCount > limit
                ? limit
                : totalCount
            : totalCount;
    const displayShowMoreButton: boolean =
        !limit || !!(limit && limitedArticles.length < limit);

    return (
        <ArticlePreviewListUI
            source={source}
            showPagination={showPagination}
            articles={limitedArticles}
            total={total}
            displayShowMoreButton={displayShowMoreButton}
            hasNextPage={!!hasNextPage}
            fetchMore={fetchMore}
        />
    );
};

export default ArticlePreviewList;
